import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productAPI } from '../../services/api';

// Async Thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({ page = 1, limit = 20, category, search, filters, sort } = {}, { rejectWithValue }) => {
    try {
      const response = await productAPI.getProducts({
        page,
        limit,
        category,
        search,
        filters,
        sort,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await productAPI.getProductById(productId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productAPI.getCategories();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productAPI.getFeaturedProducts();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured products');
    }
  }
);

export const searchProducts = createAsyncThunk(
  'products/searchProducts',
  async ({ query, filters }, { rejectWithValue }) => {
    try {
      const response = await productAPI.searchProducts({ query, filters });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Search failed');
    }
  }
);

export const addProductReview = createAsyncThunk(
  'products/addProductReview',
  async ({ productId, review }, { rejectWithValue }) => {
    try {
      const response = await productAPI.addReview(productId, review);
      return { productId, review: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add review');
    }
  }
);

// Initial State
const initialState = {
  products: [],
  currentProduct: null,
  featuredProducts: [],
  categories: [],
  searchResults: [],
  loading: false,
  error: null,
  filters: {
    category: '',
    priceRange: [0, 100000],
    capacity: '',
    warranty: '',
    inStock: false,
    payGEnabled: false,
    adminApproved: false,
  },
  sort: 'popularity',
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    hasMore: false,
  },
  searchQuery: '',
  isSearching: false,
};

// Product Slice
const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
      state.isSearching = false;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.sort = 'popularity';
    },
    setCurrentProduct: (state, action) => {
      state.currentProduct = action.payload;
    },
    updateProductInList: (state, action) => {
      const { productId, updates } = action.payload;
      const productIndex = state.products.findIndex(p => p._id === productId);
      if (productIndex !== -1) {
        state.products[productIndex] = { ...state.products[productIndex], ...updates };
      }
      if (state.currentProduct && state.currentProduct._id === productId) {
        state.currentProduct = { ...state.currentProduct, ...updates };
      }
    },
    addToWishlist: (state, action) => {
      const productId = action.payload;
      const product = state.products.find(p => p._id === productId);
      if (product) {
        product.isWishlisted = true;
      }
      if (state.currentProduct && state.currentProduct._id === productId) {
        state.currentProduct.isWishlisted = true;
      }
    },
    removeFromWishlist: (state, action) => {
      const productId = action.payload;
      const product = state.products.find(p => p._id === productId);
      if (product) {
        product.isWishlisted = false;
      }
      if (state.currentProduct && state.currentProduct._id === productId) {
        state.currentProduct.isWishlisted = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        const { products, pagination } = action.payload;
        
        if (action.meta.arg?.page === 1) {
          state.products = products;
        } else {
          state.products = [...state.products, ...products];
        }
        
        state.pagination = pagination;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      
      // Fetch Featured Products
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.featuredProducts = action.payload;
      })
      
      // Search Products
      .addCase(searchProducts.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload.products;
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload;
      })
      
      // Add Product Review
      .addCase(addProductReview.fulfilled, (state, action) => {
        const { productId, review } = action.payload;
        if (state.currentProduct && state.currentProduct._id === productId) {
          state.currentProduct.reviews = state.currentProduct.reviews || [];
          state.currentProduct.reviews.unshift(review);
          // Update average rating
          const totalRating = state.currentProduct.reviews.reduce((sum, r) => sum + r.rating, 0);
          state.currentProduct.rating = totalRating / state.currentProduct.reviews.length;
        }
      });
  },
});

export const {
  clearError,
  setFilters,
  setSort,
  setSearchQuery,
  clearSearchResults,
  resetFilters,
  setCurrentProduct,
  updateProductInList,
  addToWishlist,
  removeFromWishlist,
} = productSlice.actions;

export default productSlice.reducer;